import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const FAQS = [
  { q: 'What is online CUET coaching?', a: 'Online CUET coaching is structured CUET preparation delivered digitally — live classes, recorded lectures, study material, and mock tests — without needing to attend a physical coaching centre.' },
  { q: 'Is online CUET coaching effective?', a: 'Yes, when it includes live interaction, regular mock testing, and doubt resolution, online coaching can be just as effective as offline coaching, with the added benefit of flexibility.' },
  { q: 'Which is the best online CUET coaching?', a: "The right fit depends on faculty quality, mock test access, and mentorship support. BESST offers all three, built specifically around CUET's format." },
  { q: 'How do online CUET classes work?', a: 'Students attend live classes over video, access recorded sessions and study material digitally, and take regular mock tests with performance tracking throughout the course.' },
  { q: 'Can I prepare for CUET completely online?', a: "Yes, CUET preparation — including live teaching, practice, mock testing, and mentorship — can be done entirely online through a structured programme like BESST's." },
  { q: 'Does BESST offer live online CUET classes?', a: 'Yes, BESST conducts live, interactive classes across all CUET sections.' },
  { q: 'Are recorded classes available?', a: 'Yes, every live class is recorded and made available for revision at any time.' },
  { q: 'Does BESST provide mock tests?', a: "Yes, BESST's coaching includes regular full-length and topic-wise mock tests with detailed performance reports." },
  { q: 'Is mentorship included in the course?', a: 'Yes, personalised one-to-one mentorship is included alongside group classes.' },
  { q: 'Can I access study materials online?', a: 'Yes, all study materials are provided digitally and can be accessed anytime through the course.' },
  { q: 'How much does online CUET coaching cost?', a: "Course fees vary by programme structure. BESST's online CUET coaching is priced to stay affordable — specific details are available on enrolment." },
  { q: 'Can I prepare for CUET alongside my board exams?', a: "Yes, BESST's schedules are designed to work around board exam preparation rather than compete with it." },
  { q: 'How long should I prepare for CUET?', a: "Most students benefit from at least 4–6 months of structured preparation, depending on their starting point and number of domain subjects." },
  { q: "Who should join BESST's Online CUET Coaching?", a: 'Class XII students, repeat aspirants, students balancing board exams, and anyone without access to specialised CUET coaching nearby.' },
  { q: "How can I enrol in BESST's Online CUET Coaching?", a: "Students can enrol through BESST's website, after which an orientation session helps set up the right study plan." },
];

const FEATURES = [
  { icon: '🎓', title: 'Experienced CUET Faculty', desc: "Faculty who teach specifically to CUET's structure, not a generalised board-exam curriculum adapted after the fact." },
  { icon: '📡', title: 'Live Interactive Online Classes', desc: 'Real-time classes with direct faculty interaction, not pre-recorded content dressed up as a "live" course.' },
  { icon: '🎬', title: 'Recorded Lecture Access', desc: 'Every session is recorded, so nothing is lost if a student misses a class or wants to revisit a topic before a mock test.' },
  { icon: '📚', title: 'Comprehensive Study Materials', desc: 'Structured, syllabus-aligned notes that remove the need to gather resources from scattered sources.' },
  { icon: '📋', title: 'Topic-wise Practice Questions', desc: 'Focused practice after each topic, so gaps are caught early rather than surfacing for the first time in a mock test.' },
  { icon: '📄', title: 'Previous Year Question Papers', desc: "Dedicated PYQ practice sessions that build familiarity with NTA's actual question patterns." },
  { icon: '📝', title: 'Regular Mock Tests', desc: 'A consistent testing schedule throughout the course, not just a handful of mocks in the final weeks.' },
  { icon: '🤝', title: 'Personalised Mentorship', desc: "One-to-one guidance built around each student's specific weak areas and target universities." },
  { icon: '📊', title: 'Performance Analysis & Feedback', desc: 'Detailed reports after every test, turning results into a clear action plan rather than just a score.' },
  { icon: '💬', title: 'Doubt Clearing Sessions', desc: 'Dedicated time outside class hours so doubts get resolved quickly, not weeks later.' },
  { icon: '🏫', title: 'Career & Admission Guidance', desc: 'Support that continues past the exam itself, into course and university selection.' },
  { icon: '💰', title: 'Affordable Fee Structure', desc: 'Priced to stay accessible without cutting corners on faculty quality or course content.' },
];

const STEPS = [
  { n: '01', title: 'Register for the Course', desc: 'Enrol in the CUET Online Coaching Programme on the BESST platform.' },
  { n: '02', title: 'Orientation Session', desc: "Map out a personalised study plan based on the student's current level and target universities." },
  { n: '03', title: 'Join Live Classes', desc: 'Attend live interactive classes covering the full CUET syllabus section by section.' },
  { n: '04', title: 'Access Digital Study Materials', desc: 'Structured notes and practice sheets aligned to each topic as it is taught.' },
  { n: '05', title: 'Topic-wise Tests', desc: 'Practise focused tests after each topic to reinforce concepts as they are covered.' },
  { n: '06', title: 'Weekly Mock Tests', desc: 'Regular full-length and sectional mocks to build consistent exam familiarity.' },
  { n: '07', title: 'Track Your Performance', desc: 'Detailed analytics after every test turn results into a clear action plan.' },
  { n: '08', title: 'Resolve Doubts with Mentors', desc: 'Dedicated sessions to clear doubts quickly, before they accumulate.' },
  { n: '09', title: 'Revise Using Recorded Classes', desc: 'Recorded sessions remain available throughout, particularly useful in the final revision phase.' },
  { n: '10', title: 'Appear for the CUET Exam', desc: 'Walk in with genuine familiarity, having faced the format many times over.' },
];

const CHOOSE_FACTORS = [
  { icon: '👨‍🏫', title: 'Experienced Faculty', desc: "Faculty should have specific CUET teaching experience — not just subject expertise applied generically. BESST's faculty are built around exactly this." },
  { icon: '📖', title: 'Comprehensive Curriculum', desc: "The course should cover every CUET section in depth, not just the subjects that are easiest to teach. BESST's curriculum is structured section by section, matching the actual exam." },
  { icon: '📡', title: 'Live Classes', desc: 'Real-time interaction matters — a purely pre-recorded course leaves doubts unresolved. BESST runs live classes as the core of its programme.' },
  { icon: '📝', title: 'Mock Test Series', desc: 'Regular, realistic mock testing is one of the strongest predictors of exam performance. BESST builds mock tests into the course from the start, not just at the end.' },
  { icon: '📊', title: 'Performance Tracking', desc: 'Look for a course that turns test results into actionable feedback. BESST provides detailed performance analytics after every test.' },
  { icon: '💬', title: 'Doubt Resolution', desc: 'Doubts should be resolved quickly, not left to pile up. BESST runs dedicated doubt-clearing sessions outside regular class time.' },
  { icon: '🤝', title: 'Student Support', desc: 'Beyond academics, ongoing mentorship and guidance matter — BESST pairs every student with personalised mentorship through the programme.' },
  { icon: '💰', title: 'Affordable Fees', desc: "Quality preparation shouldn't be priced out of reach. BESST's fee structure is built to stay accessible." },
];

const TOC = [
  { id: 'what-is', label: 'What is Online CUET Coaching?' },
  { id: 'why-choose-online', label: 'Why Choose Online?' },
  { id: 'why-besst', label: 'Why BESST Stands Out' },
  { id: 'what-learn', label: 'What You Will Learn' },
  { id: 'features-table', label: 'Features' },
  { id: 'comparison', label: 'Online vs Offline' },
  { id: 'who-should', label: 'Who Should Join?' },
  { id: 'how-works', label: 'How It Works' },
  { id: 'how-choose', label: 'How to Choose' },
  { id: 'faqs', label: 'FAQs' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://besst.in' },
    { '@type': 'ListItem', position: 2, name: 'CUET Coaching', item: 'https://besst.in/cuet-coaching' },
    { '@type': 'ListItem', position: 3, name: 'Online CUET Coaching', item: 'https://besst.in/online-cuet-coaching' },
  ],
};

const CSS = `
  .oc-page{background:linear-gradient(160deg,#0c0420 0%,#140831 50%,#0c0420 100%);min-height:100vh;color:#e8deff;font-family:'Inter','Segoe UI',system-ui,sans-serif;}
  .oc-breadcrumb{max-width:1200px;margin:0 auto;padding:1rem 1.5rem 0;font-size:.8rem;color:#9ca3af;display:flex;align-items:center;gap:.4rem;flex-wrap:wrap;}
  .oc-breadcrumb a{color:#a78bfa;text-decoration:none;}.oc-breadcrumb a:hover{color:#d4af37;}
  .oc-hero{padding:3rem 1.5rem 3.5rem;max-width:860px;margin:0 auto;text-align:center;}
  .oc-badge{display:inline-block;background:rgba(212,175,55,.12);color:#d4af37;border:1px solid rgba(212,175,55,.3);border-radius:20px;padding:.35rem 1rem;font-size:.78rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:1.25rem;}
  .oc-hero h1{font-size:clamp(1.75rem,4vw,2.7rem);font-weight:800;line-height:1.2;margin:0 0 1.25rem;color:#f0e6ff;}
  .oc-hero h1 em{font-style:normal;background:linear-gradient(90deg,#d4af37,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
  .oc-intro{font-size:1.05rem;line-height:1.8;color:#c4b5fd;max-width:720px;margin:0 auto 2rem;}
  .oc-cta{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;}
  .btn-gold{background:linear-gradient(135deg,#d4af37,#f59e0b);color:#0c0420;font-weight:700;padding:.85rem 2rem;border-radius:10px;border:none;cursor:pointer;font-size:.95rem;text-decoration:none;display:inline-block;transition:transform .2s,box-shadow .2s;}
  .btn-gold:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(212,175,55,.35);}
  .btn-outline{background:transparent;color:#e8deff;font-weight:600;padding:.85rem 2rem;border-radius:10px;border:1.5px solid rgba(232,222,255,.25);cursor:pointer;font-size:.95rem;text-decoration:none;display:inline-block;transition:border-color .2s,background .2s;}
  .btn-outline:hover{border-color:#a78bfa;background:rgba(167,139,250,.08);}
  .oc-highlights{background:rgba(255,255,255,.03);border-top:1px solid rgba(255,255,255,.06);border-bottom:1px solid rgba(255,255,255,.06);padding:1.5rem;}
  .oc-highlights-inner{max-width:1000px;margin:0 auto;display:grid;grid-template-columns:repeat(4,1fr);gap:1rem;text-align:center;}
  @media(max-width:640px){.oc-highlights-inner{grid-template-columns:repeat(2,1fr);}}
  .oc-hval{font-size:1.6rem;font-weight:800;color:#d4af37;}.oc-hlbl{font-size:.78rem;color:#9ca3af;margin-top:2px;}
  .oc-layout{max-width:1200px;margin:0 auto;padding:3rem 1.5rem;display:grid;grid-template-columns:220px 1fr;gap:3rem;align-items:start;}
  @media(max-width:900px){.oc-layout{grid-template-columns:1fr;}}
  .oc-toc{position:sticky;top:80px;}
  @media(max-width:900px){.oc-toc{display:none;}}
  .oc-toc-title{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#7c6b9e;margin-bottom:.75rem;}
  .oc-toc ul{list-style:none;padding:0;margin:0;}.oc-toc li{margin-bottom:.15rem;}
  .oc-toc button{background:none;border:none;cursor:pointer;text-align:left;width:100%;padding:.4rem .6rem;font-size:.82rem;color:#9ca3af;border-radius:6px;border-left:2px solid transparent;transition:all .15s;line-height:1.4;}
  .oc-toc button:hover{color:#e8deff;background:rgba(255,255,255,.05);}
  .oc-toc button.active{color:#d4af37;border-left-color:#d4af37;background:rgba(212,175,55,.07);font-weight:600;}
  .oc-section{margin-bottom:3.5rem;scroll-margin-top:90px;}
  .oc-h2{font-size:clamp(1.25rem,2.5vw,1.65rem);font-weight:700;color:#f0e6ff;margin:0 0 1.25rem;padding-bottom:.6rem;border-bottom:1px solid rgba(212,175,55,.2);}
  .oc-h3{font-size:1.02rem;font-weight:700;color:#d4af37;margin:1.25rem 0 .45rem;}
  .oc-p{font-size:.97rem;line-height:1.8;color:#c4b5fd;margin:0 0 1rem;}
  .oc-parent-link{display:inline-flex;align-items:center;gap:.4rem;font-size:.82rem;color:#7c6b9e;text-decoration:none;margin-bottom:.75rem;}
  .oc-parent-link:hover{color:#a78bfa;}
  .oc-bullets{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.65rem;}
  .oc-bullets li{display:flex;gap:.75rem;align-items:flex-start;font-size:.95rem;line-height:1.65;color:#c4b5fd;}
  .oc-bi{width:22px;height:22px;background:rgba(212,175,55,.15);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.7rem;color:#d4af37;flex-shrink:0;margin-top:2px;}
  .oc-bs{color:#e8deff;font-weight:600;}
  .oc-why-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:1rem;}
  @media(max-width:600px){.oc-why-grid{grid-template-columns:1fr;}}
  .oc-why-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:12px;padding:1.25rem;transition:border-color .2s;}
  .oc-why-card:hover{border-color:rgba(212,175,55,.3);}
  .oc-why-icon{font-size:1.5rem;margin-bottom:.5rem;}
  .oc-why-card h3{font-size:.95rem;font-weight:700;color:#f0e6ff;margin:0 0 .4rem;}
  .oc-why-card p{font-size:.88rem;line-height:1.6;color:#9ca3af;margin:0;}
  .oc-feat-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:640px){.oc-feat-grid{grid-template-columns:1fr;}}
  .oc-fcard{display:flex;gap:.85rem;align-items:flex-start;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;transition:border-color .2s,background .2s;}
  .oc-fcard:hover{border-color:rgba(212,175,55,.25);background:rgba(255,255,255,.05);}
  .oc-ficon{font-size:1.4rem;width:42px;height:42px;display:flex;align-items:center;justify-content:center;background:rgba(212,175,55,.1);border-radius:8px;flex-shrink:0;}
  .oc-fcard h3{font-size:.88rem;font-weight:700;color:#f0e6ff;margin:0 0 .3rem;}
  .oc-fcard p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .oc-learn-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;}
  @media(max-width:680px){.oc-learn-grid{grid-template-columns:1fr;}}
  .oc-learn-card{background:rgba(124,58,237,.08);border:1px solid rgba(124,58,237,.2);border-radius:12px;padding:1.25rem;}
  .oc-learn-card h3{font-size:.95rem;font-weight:700;color:#a78bfa;margin:0 0 .5rem;}
  .oc-learn-card p{font-size:.87rem;line-height:1.6;color:#9ca3af;margin:0;}
  .oc-table-wrap{overflow-x:auto;border-radius:12px;}
  .oc-table{width:100%;border-collapse:collapse;font-size:.9rem;}
  .oc-table thead tr{background:rgba(212,175,55,.12);}
  .oc-table th{padding:.85rem 1.25rem;text-align:left;font-weight:700;color:#d4af37;border:1px solid rgba(212,175,55,.15);font-size:.85rem;letter-spacing:.04em;}
  .oc-table td{padding:.75rem 1.25rem;border:1px solid rgba(255,255,255,.06);color:#c4b5fd;}
  .oc-table tbody tr:nth-child(even){background:rgba(255,255,255,.025);}
  .oc-table tbody tr:hover{background:rgba(212,175,55,.05);}
  .oc-tick{color:#22c55e;font-size:1.1rem;font-weight:700;}
  .oc-cross{color:#ef4444;font-size:1rem;}
  .oc-better{color:#22c55e;font-weight:600;}
  .oc-steps{display:flex;flex-direction:column;}
  .oc-step{display:flex;gap:1.25rem;align-items:flex-start;}
  .oc-step-left{display:flex;flex-direction:column;align-items:center;flex-shrink:0;}
  .oc-step-num{width:44px;height:44px;border-radius:50%;background:linear-gradient(135deg,rgba(212,175,55,.25),rgba(212,175,55,.1));border:2px solid rgba(212,175,55,.4);display:flex;align-items:center;justify-content:center;font-size:.78rem;font-weight:800;color:#d4af37;flex-shrink:0;}
  .oc-step-line{width:2px;flex:1;min-height:28px;background:linear-gradient(to bottom,rgba(212,175,55,.3),rgba(212,175,55,.05));}
  .oc-step:last-child .oc-step-line{display:none;}
  .oc-step-body{padding:.6rem 0 1.5rem;}
  .oc-step-body h3{font-size:.97rem;font-weight:700;color:#f0e6ff;margin:0 0 .25rem;}
  .oc-step-body p{font-size:.87rem;line-height:1.6;color:#9ca3af;margin:0;}
  .oc-choose-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:640px){.oc-choose-grid{grid-template-columns:1fr;}}
  .oc-choose-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;display:flex;gap:.85rem;align-items:flex-start;transition:border-color .2s;}
  .oc-choose-card:hover{border-color:rgba(167,139,250,.3);}
  .oc-choose-icon{font-size:1.3rem;width:38px;height:38px;display:flex;align-items:center;justify-content:center;background:rgba(124,58,237,.12);border-radius:8px;flex-shrink:0;}
  .oc-choose-card h3{font-size:.88rem;font-weight:700;color:#f0e6ff;margin:0 0 .3rem;}
  .oc-choose-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .oc-tips-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.65rem;}
  @media(max-width:600px){.oc-tips-grid{grid-template-columns:1fr;}}
  .oc-tip{display:flex;gap:.65rem;align-items:flex-start;background:rgba(255,255,255,.03);border-radius:8px;padding:.75rem .9rem;font-size:.88rem;line-height:1.55;color:#c4b5fd;border:1px solid rgba(255,255,255,.06);}
  .oc-tip-check{color:#22c55e;flex-shrink:0;margin-top:1px;}
  .oc-faq-list{display:flex;flex-direction:column;gap:.6rem;}
  .oc-faq-item{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-radius:10px;overflow:hidden;transition:border-color .2s;}
  .oc-faq-item.open{border-color:rgba(212,175,55,.3);}
  .oc-faq-q{width:100%;background:none;border:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:1rem;padding:1rem 1.25rem;text-align:left;font-size:.93rem;font-weight:600;color:#e8deff;transition:color .15s;}
  .oc-faq-q:hover{color:#d4af37;}
  .oc-faq-chevron{font-size:.75rem;color:#7c6b9e;transition:transform .25s;flex-shrink:0;}
  .oc-faq-item.open .oc-faq-chevron{transform:rotate(180deg);color:#d4af37;}
  .oc-faq-a{max-height:0;overflow:hidden;transition:max-height .3s ease;}
  .oc-faq-item.open .oc-faq-a{max-height:500px;}
  .oc-faq-a-inner{padding:.85rem 1.25rem 1rem;font-size:.88rem;line-height:1.75;color:#9ca3af;border-top:1px solid rgba(255,255,255,.06);}
  .oc-links-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.65rem;}
  @media(max-width:540px){.oc-links-grid{grid-template-columns:1fr;}}
  .oc-ilink{display:flex;align-items:center;gap:.6rem;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:8px;padding:.7rem 1rem;text-decoration:none;color:#a78bfa;font-size:.88rem;font-weight:500;transition:border-color .2s,background .2s;}
  .oc-ilink:hover{border-color:rgba(167,139,250,.4);background:rgba(167,139,250,.06);color:#c4b5fd;}
  .oc-cta-banner{background:linear-gradient(135deg,rgba(124,58,237,.25) 0%,rgba(212,175,55,.12) 100%);border:1px solid rgba(212,175,55,.2);border-radius:16px;padding:2.5rem;text-align:center;margin-top:1rem;}
  .oc-cta-banner h2{font-size:1.5rem;font-weight:800;color:#f0e6ff;margin:0 0 .75rem;}
  .oc-cta-banner p{font-size:.95rem;line-height:1.7;color:#c4b5fd;max-width:600px;margin:0 auto 1.5rem;}
`;

const OnlineCuetCoachingPage = () => {
  const [activeSection, setActiveSection] = useState('what-is');
  const [openFaq, setOpenFaq] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    TOC.forEach(t => { const el = document.getElementById(t.id); if (el) observerRef.current.observe(el); });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <Helmet>
        <title>Online CUET Coaching | Live CUET Online Classes | BESST</title>
        <meta name="description" content="Join BESST's Online CUET Coaching with live classes, expert faculty, mock tests, study materials, and personalised mentorship to prepare for CUET from anywhere in India." />
        <meta name="keywords" content="online cuet coaching, online cuet classes, best online cuet coaching, online coaching for cuet, cuet online preparation, online coaching for cuet exam, online cuet coaching classes, cuet online course, cuet preparation online, live online cuet classes" />
        <meta property="og:title" content="Online CUET Coaching | Live CUET Online Classes | BESST" />
        <meta property="og:description" content="Join BESST's Online CUET Coaching with live classes, expert faculty, mock tests, study materials, and personalised mentorship to prepare for CUET from anywhere in India." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://besst.in/online-cuet-coaching" />
        <link rel="canonical" href="https://besst.in/online-cuet-coaching" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="oc-page mt-20">
        <style>{CSS}</style>

        {/* Breadcrumb */}
        <nav className="oc-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link><span>›</span>
          <Link to="/cuet-coaching">CUET Coaching</Link><span>›</span>
          <span style={{ color: '#e8deff' }}>Online CUET Coaching</span>
        </nav>

        {/* Hero */}
        <div className="oc-hero">
          <div className="oc-badge">Live Online Classes · Anywhere in India</div>
          <h1>
            <em>Online CUET Coaching:</em> Prepare for CUET Anytime, Anywhere with BESST
          </h1>
          <p className="oc-intro">
            Online learning has become the default choice for a large share of CUET aspirants — and it isn't hard to see why. BESST's Online CUET Coaching was built around a simple idea: a flexible, structured, result-oriented preparation experience that works whether a student is in a metro city or several hours from the nearest coaching centre.
          </p>
          <div className="oc-cta">
            <Link to="/subscriptions" className="btn-gold">Join Online CUET Coaching →</Link>
            <Link to="/cuet-mock-test" className="btn-outline">Try a Free Mock Test</Link>
          </div>
        </div>

        {/* Highlights bar */}
        <div className="oc-highlights">
          <div className="oc-highlights-inner">
            <div><div className="oc-hval">Live</div><div className="oc-hlbl">Interactive Classes</div></div>
            <div><div className="oc-hval">100%</div><div className="oc-hlbl">Online — No Travel</div></div>
            <div><div className="oc-hval">4–6</div><div className="oc-hlbl">Months Structured Prep</div></div>
            <div><div className="oc-hval">1:1</div><div className="oc-hlbl">Personalised Mentorship</div></div>
          </div>
        </div>

        {/* Layout */}
        <div className="oc-layout">

          {/* TOC */}
          <aside className="oc-toc" aria-label="Table of contents">
            <div className="oc-toc-title">On This Page</div>
            <ul>
              {TOC.map(t => (
                <li key={t.id}>
                  <button className={activeSection === t.id ? 'active' : ''} onClick={() => scrollTo(t.id)}>{t.label}</button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Main content */}
          <main>
            <Link to="/cuet-coaching" className="oc-parent-link">← Back to CUET Coaching</Link>

            {/* Intro para */}
            <p className="oc-p" style={{ marginBottom: '2rem' }}>
              Online CUET coaching is exam preparation delivered entirely over the internet — live classes, recorded lectures, mock tests, and mentorship, without needing to attend a physical centre. BESST delivers this through live interactive classes, regular mock testing, and personalised mentorship for students anywhere in India.
            </p>

            {/* What is */}
            <section id="what-is" className="oc-section">
              <h2 className="oc-h2">What is Online CUET Coaching?</h2>
              <p className="oc-p">Online CUET coaching is structured exam preparation delivered digitally — through live classes, recorded lectures, study material, and mock tests — rather than in a physical classroom. Functionally, it covers the same ground as offline coaching: syllabus coverage, practice, doubt resolution, and testing. What changes is delivery, not depth.</p>

              <h3 className="oc-h3">How it differs from offline coaching</h3>
              <p className="oc-p">Offline coaching ties a student to a physical location and a fixed class schedule. Online coaching removes both constraints — students attend from wherever they are, and recorded sessions mean a missed class doesn't mean a missed concept.</p>

              <h3 className="oc-h3">Why students are increasingly choosing online learning</h3>
              <p className="oc-p">Beyond convenience, students are choosing online coaching because it tends to offer more mock test access, more flexible scheduling around board exams, and often a lower overall cost than a comparable offline programme.</p>

              <h3 className="oc-h3">Advantages of digital learning platforms</h3>
              <p className="oc-p">Digital platforms also make performance tracking easier — every test a student takes can be logged, analysed, and turned into a clear picture of where they stand, something that's much harder to maintain consistently in a traditional classroom setting.</p>
            </section>

            {/* Why choose online */}
            <section id="why-choose-online" className="oc-section">
              <h2 className="oc-h2">Why Choose Online CUET Coaching?</h2>
              <div className="oc-why-grid">
                {[
                  ['🌍', 'Learn from Anywhere in India', "Students don't need to live near a coaching centre — the same classes, faculty, and mock tests are accessible from any city, town, or district."],
                  ['🕐', 'Flexible Study Schedule', 'Classes can be scheduled around board exam revision instead of forcing students to choose between the two.'],
                  ['📡', 'Live Interactive Classes', 'Real-time classes let students ask questions as concepts come up, rather than saving doubts for later.'],
                  ['🎬', 'Recorded Sessions for Revision', "Every class stays accessible afterward, so revision doesn't depend on notes taken in the moment."],
                  ['📈', 'Learn at Your Own Pace', 'Students can slow down on difficult topics and move faster through ones they have already mastered.'],
                  ['💰', 'Affordable vs Offline Coaching', "Without the cost of a physical centre, online coaching is typically priced more accessibly."],
                  ['👨‍🏫', 'Access to Expert Faculty', 'Location stops being a limiting factor — students get access to faculty they might never have been able to reach through an offline-only option.'],
                  ['📱', 'Mobile-Friendly Learning', 'Access classes, materials, and mock tests on any device, at any time.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="oc-why-card">
                    <div className="oc-why-icon">{icon}</div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Why BESST */}
            <section id="why-besst" className="oc-section">
              <h2 className="oc-h2">Why BESST's Online CUET Coaching Stands Out</h2>
              <div className="oc-feat-grid">
                {FEATURES.map(f => (
                  <div key={f.title} className="oc-fcard">
                    <div className="oc-ficon">{f.icon}</div>
                    <div><h3>{f.title}</h3><p>{f.desc}</p></div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
                <Link to="/subscriptions" className="btn-gold">Book a Free Counselling Session →</Link>
              </div>
            </section>

            {/* What you learn */}
            <section id="what-learn" className="oc-section">
              <h2 className="oc-h2">What You'll Learn in BESST's Online CUET Classes</h2>
              <div className="oc-learn-grid">
                {[
                  ['Language Test Preparation', "Reading comprehension, vocabulary, and language-specific question formats practised in CUET's actual style."],
                  ['Domain Subject Preparation', 'Subject-wise coverage aligned to the CUET domain syllabus, with focus on high-weightage areas.'],
                  ['General Test Preparation', 'Structured coverage of the general knowledge, reasoning, and aptitude sections tested in CUET\'s General Test.'],
                ].map(([title, desc]) => (
                  <div key={title} className="oc-learn-card"><h3>{title}</h3><p>{desc}</p></div>
                ))}
              </div>
            </section>

            {/* Features table */}
            <section id="features-table" className="oc-section">
              <h2 className="oc-h2">Features of BESST's Online CUET Coaching</h2>
              <div className="oc-table-wrap">
                <table className="oc-table">
                  <thead><tr><th>Feature</th><th>BESST Online CUET Coaching</th></tr></thead>
                  <tbody>
                    {['Live Interactive Classes','Recorded Lectures','Expert Faculty','Mock Tests','Previous Year Papers','Performance Reports','Personal Mentorship','Doubt Clearing','Digital Study Materials','Mobile-Friendly Learning','Career Guidance'].map(f => (
                      <tr key={f}><td>{f}</td><td><span className="oc-tick">✔</span></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="oc-section">
              <h2 className="oc-h2">Online CUET Coaching vs Offline Coaching</h2>
              <div className="oc-table-wrap">
                <table className="oc-table">
                  <thead><tr><th>Feature</th><th>Online Coaching</th><th>Offline Coaching</th></tr></thead>
                  <tbody>
                    {[
                      ['Flexibility', 'High', 'Limited'],
                      ['Learn from Home', '✔', '✘'],
                      ['Recorded Classes', '✔', 'Rarely Available'],
                      ['Travel Required', '✘', '✔'],
                      ['Expert Faculty Access', '✔', 'Depends on Location'],
                      ['Cost', 'More Affordable', 'Usually Higher'],
                      ['Study at Your Own Pace', '✔', 'Limited'],
                    ].map(([feature, online, offline]) => (
                      <tr key={feature}>
                        <td style={{ fontWeight: 600, color: '#e8deff' }}>{feature}</td>
                        <td className="oc-better">{online}</td>
                        <td style={{ color: '#9ca3af' }}>{offline}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="oc-p" style={{ marginTop: '1rem' }}>
                For most students today, this table explains the shift toward online coaching fairly directly — it isn't that offline coaching has gotten worse, it's that online coaching now matches it on faculty quality and mock test access while removing the constraints of location, travel, and rigid scheduling.
              </p>
            </section>

            {/* Who should join */}
            <section id="who-should" className="oc-section">
              <h2 className="oc-h2">Who Should Join BESST's Online CUET Coaching?</h2>
              <ul className="oc-bullets">
                {[
                  'Class XII students preparing for CUET while managing board exams',
                  'Repeat aspirants aiming to improve on a previous attempt',
                  'Students preparing alongside Board Exams, who need a schedule built around both',
                  'Students in cities without specialised CUET coaching, where offline options are limited or unavailable',
                  'Students seeking flexible study schedules that fit around other commitments',
                  'Working aspirants or gap-year students who need a course that does not depend on fixed class timings',
                ].map(t => <li key={t}><span className="oc-bi">→</span><span>{t}</span></li>)}
              </ul>
            </section>

            {/* How it works */}
            <section id="how-works" className="oc-section">
              <h2 className="oc-h2">How BESST's Online CUET Coaching Works</h2>
              <div className="oc-steps">
                {STEPS.map(s => (
                  <div key={s.n} className="oc-step">
                    <div className="oc-step-left">
                      <div className="oc-step-num">{s.n}</div>
                      <div className="oc-step-line" />
                    </div>
                    <div className="oc-step-body"><h3>{s.title}</h3><p>{s.desc}</p></div>
                  </div>
                ))}
              </div>
            </section>

            {/* How to choose */}
            <section id="how-choose" className="oc-section">
              <h2 className="oc-h2">How to Choose the Best Online CUET Coaching</h2>
              <p className="oc-p">Choosing between online CUET coaching options comes down to a few concrete factors, not marketing claims:</p>
              <div className="oc-choose-grid">
                {CHOOSE_FACTORS.map(f => (
                  <div key={f.title} className="oc-choose-card">
                    <div className="oc-choose-icon">{f.icon}</div>
                    <div><h3>{f.title}</h3><p>{f.desc}</p></div>
                  </div>
                ))}
              </div>
            </section>

            {/* Why students choose */}
            <section className="oc-section">
              <h2 className="oc-h2">Why Students Choose BESST's Online CUET Classes</h2>
              <div className="oc-tips-grid">
                {[
                  'Student-focused teaching methodology built around how students actually learn, not a fixed lecture format',
                  'Structured learning plans paced against the real exam timeline',
                  'Regular assessments that keep progress visible throughout the course',
                  'Personalised mentorship alongside group classes',
                  'Flexible learning environment that fits around board exams and other commitments',
                  'Affordable pricing without a drop in faculty quality',
                  "Proven preparation framework built specifically around CUET's format, not adapted from another exam",
                ].map(tip => (
                  <div key={tip} className="oc-tip"><span className="oc-tip-check">✓</span><span>{tip}</span></div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section id="faqs" className="oc-section">
              <h2 className="oc-h2">Frequently Asked Questions (FAQs)</h2>
              <div className="oc-faq-list">
                {FAQS.map((f, i) => (
                  <div key={i} className={`oc-faq-item ${openFaq === i ? 'open' : ''}`}>
                    <button className="oc-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                      <span>{f.q}</span><span className="oc-faq-chevron">▼</span>
                    </button>
                    <div className="oc-faq-a" aria-hidden={openFaq !== i}>
                      <div className="oc-faq-a-inner">{f.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Internal links */}
            <section className="oc-section">
              <h2 className="oc-h2">Explore More CUET Resources</h2>
              <div className="oc-links-grid">
                {[
                  ['/cuet-coaching', '📚 CUET Coaching (Pillar)'],
                  ['/best-cuet-coaching-in-assam', '📍 Best CUET Coaching in Assam'],
                  ['/cuet-preparation-strategy', '🎯 CUET Preparation Strategy'],
                  ['/cuet-study-plan', '🗓️ CUET Study Plan'],
                  ['/cuet-mock-test', '📝 CUET Mock Test'],
                  ['/cuet-mock-test/free', '🆓 Free CUET Mock Test'],
                  ['/cuet-syllabus', '📋 CUET Syllabus'],
                  ['/cuet-exam-pattern', '📐 CUET Exam Pattern'],
                  ['/cuet-registration', '📝 CUET Registration'],
                  ['/cuet-cutoff', '📊 CUET Cutoff'],
                  ['/cuet-counselling', '🎓 CUET Counselling'],
                  ['/cuet-previous-year-question-papers', '📄 CUET PYQ Papers'],
                ].map(([to, label]) => (
                  <Link key={to} to={to} className="oc-ilink">
                    <span>{label}</span>
                    <span style={{ marginLeft: 'auto', opacity: .5 }}>→</span>
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div className="oc-cta-banner">
              <h2>Start Your CUET Preparation with BESST's Online Coaching</h2>
              <p>
                Learning with BESST means expert guidance, structured classes, and consistent mock testing — all without the cost and disruption of relocating or commuting. Join BESST's Online CUET Coaching today and prepare for success with live interactive classes, experienced faculty, comprehensive study materials, regular mock tests, and personalised mentorship.
              </p>
              <Link to="/subscriptions" className="btn-gold">Join BESST's Online CUET Coaching →</Link>
            </div>

          </main>
        </div>
      </div>
    </>
  );
};

export default OnlineCuetCoachingPage;
